import { type CSSProperties, type ElementType, type ReactNode, useMemo } from 'react';
import { clsx } from 'clsx';
import type { Space } from '../../styles/tokens';
import { buildResponsiveProps, type ResponsiveValue } from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import type { Polymorphic } from '../polymorphism/polymorphic';
import styles from './vertical-stack.module.scss';

export type VerticalStackAlign = 'start' | 'center' | 'end' | 'space-around' | 'space-between' | 'space-evenly';

export type VerticalStackInlineAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';

export const DEFAULT_VERTICAL_STACK_ELEMENT = 'div';

export type DefaultVerticalStackElement = typeof DEFAULT_VERTICAL_STACK_ELEMENT;

export const VERTICAL_STACK_COMPONENT_STYLE_NAME = 'vertical-stack';

interface BaseVerticalStackProps {
  // align controls the vertical alignment of children
  align?: VerticalStackAlign;
  children?: ReactNode;
  className?: string;
  // inlineAlign controls the horizontal alignment of children
  inlineAlign?: VerticalStackInlineAlign;
  // gap controls the spacing between children
  gap?: ResponsiveValue<Space>;
  // reversed controls if the children render in their provided order or reversed
  reversed?: boolean;
}

export type VerticalStackProps<AsComponent extends ElementType = DefaultVerticalStackElement> = Polymorphic.Props<
  AsComponent,
  BaseVerticalStackProps
>;

export function VerticalStack<AsComponent extends ElementType = DefaultVerticalStackElement>({
  align,
  as,
  children,
  className,
  gap,
  inlineAlign,
  reversed,
  style,
  ...rest
}: VerticalStackProps<AsComponent>) {
  const Component = (as ?? DEFAULT_VERTICAL_STACK_ELEMENT) satisfies ElementType;
  const baseStyle = useMemo(
    () =>
      sanitizeCSSVariables({
        ...style,
        ...buildResponsiveProps(VERTICAL_STACK_COMPONENT_STYLE_NAME, 'gap', gap, 'space'),
      } as CSSProperties),
    [style, gap],
  );

  return (
    <Component
      {...rest}
      className={clsx(styles.verticalStack, className, styles[`align-${align}`], styles[`inline-align-${inlineAlign}`], {
        [styles.itemsReverse]: reversed,
      })}
      style={baseStyle}
    >
      {children}
    </Component>
  );
}
